import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import type {
  CancelTransferPayload,
  ConfirmTransferPayload,
  RejectRemainingPayload,
  CreateTransferPayload,
  Transfer,
  TransferDestination,
  TransferListParams,
  TransferListResponse,
  TransferStats,
  TransferStatsParams,
} from './types';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_STATS: TransferStats = {
  total: 0,
  by_status: {
    PENDING: 0,
    IN_TRANSIT: 0,
    PARTIALLY_RECEIVED: 0,
    COMPLETED: 0,
    PARTIALLY_ACCEPTED: 0,
    RECEIVED: 0,
    REJECTED: 0,
    CANCELLED: 0,
  },
};

export const stockTransfersApi = {
  // Deliberately NOT wrapped in withFallback. An empty list and an unreachable
  // list endpoint are indistinguishable once a 404/501 is coerced to `total: 0` —
  // the page renders "No stock transfers yet" for a tenant that has transfers, and
  // no error is ever thrown for the page to react to. Let the failure propagate so
  // the list page can show an error + retry instead of a benign empty state.
  list: (params: TransferListParams) =>
    apiClientPaginated<TransferListResponse['data']>(
      `/transfers${buildQuery({
        page: params.page,
        limit: params.limit,
        status: params.status || undefined,
        location_id: params.location_id,
        search: params.search,
        start_date: params.start_date,
        end_date: params.end_date,
      })}`,
    ) as Promise<TransferListResponse>,

  // Single aggregate call that backs the KPI row — replaces one limit:1 list
  // request per status.
  stats: (params: TransferStatsParams) =>
    withFallback(
      () =>
        apiClient<TransferStats>(
          `/transfers/stats${buildQuery({ location_id: params.location_id })}`,
        ),
      EMPTY_STATS,
    ),

  getById: (id: string) =>
    // Not wrapped in withFallback — a 404 must surface so the detail page leaves its skeleton.
    apiClient<Transfer>(`/transfers/${id}`),

  // Every active tenant location — not only the caller's own: a transfer is
  // checked on its source, so a branch manager may send to any location.
  destinations: () => apiClient<TransferDestination[]>('/transfers/destinations'),

  create: (payload: CreateTransferPayload) =>
    apiClient<Transfer>('/transfers', {
      method: 'POST',
      body: payload,
    }),

  // Source dispatches a PENDING transfer — stock leaves the source, goods in transit.
  dispatch: (id: string) =>
    apiClient<Record<string, unknown>>(`/transfers/${id}/dispatch`, {
      method: 'POST',
    }),

  confirm: (id: string, payload: ConfirmTransferPayload) =>
    apiClient<Record<string, unknown>>(`/transfers/${id}/confirm`, {
      method: 'PUT',
      body: payload,
    }),

  // Writes off what is still in transit at the source and closes the transfer.
  rejectRemaining: (id: string, payload: RejectRemainingPayload) =>
    apiClient<Record<string, unknown>>(`/transfers/${id}/reject-remaining`, {
      method: 'POST',
      body: payload,
    }),

  cancel: (id: string, payload: CancelTransferPayload) =>
    apiClient<Record<string, unknown>>(`/transfers/${id}/cancel`, {
      method: 'PATCH',
      body: payload,
    }),
};
